import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Navigate, useSearch } from '@tanstack/react-router'
import { useCurrentUser, useSignIn, useSignUp } from '~/lib/auth'
import { signInSchema, signUpSchema, type AuthValues } from '~/lib/schemas'
import { LogoMark } from '~/components/Logo'
import GoogleButton from '~/components/GoogleButton'
import ThemeToggle from '~/components/ThemeToggle'
import Spinner from '~/components/Spinner'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'

type Mode = 'signin' | 'signup'

export default function Login() {
  const search = useSearch({ from: '/login' })
  const [mode, setMode] = useState<Mode>('signin')

  const { data: user, isPending } = useCurrentUser()
  const signIn = useSignIn()
  const signUp = useSignUp()

  const form = useForm<AuthValues>({
    resolver: zodResolver(mode === 'signin' ? signInSchema : signUpSchema),
    defaultValues: { email: '', password: '', name: '' },
  })

  const mutation = mode === 'signin' ? signIn : signUp
  const serverError = mutation.error?.message ?? search.error

  if (isPending) {
    return (
      <div className="grid min-h-screen place-items-center bg-plane">
        <Spinner />
      </div>
    )
  }

  if (user) return <Navigate to={search.redirect ?? '/dashboard'} replace />

  const submit = form.handleSubmit((values) => {
    if (mode === 'signin') signIn.mutate({ email: values.email, password: values.password })
    else signUp.mutate({ ...values, name: values.name ?? '' })
  })

  const swapMode = () => {
    setMode(mode === 'signin' ? 'signup' : 'signin')
    mutation.reset()
    form.clearErrors()
  }

  return (
    <div className="grid min-h-screen bg-plane lg:grid-cols-2">
      <BrandPanel />

      <div className="flex flex-col px-6 py-8 sm:px-12">
        <div className="flex justify-end">
          <ThemeToggle />
        </div>

        <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center">
          <LogoMark className="size-10 lg:hidden" />

          <h1 className="mt-6 text-2xl font-semibold tracking-tight">
            {mode === 'signin' ? 'Sign in to Sales AI' : 'Create your account'}
          </h1>
          <p className="mt-2 text-sm text-ink-2">
            {mode === 'signin'
              ? 'Use Google or your email address.'
              : 'Takes a few seconds. No credit card.'}
          </p>

          <div className="mt-8">
            <GoogleButton label={mode === 'signin' ? 'Sign in with Google' : 'Sign up with Google'} />
          </div>

          <div className="my-6 flex items-center gap-4 text-xs text-faint">
            <span className="h-px flex-1 bg-line" />
            or
            <span className="h-px flex-1 bg-line" />
          </div>

          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {mode === 'signup' && (
              <Field
                id="name"
                label="Name"
                autoComplete="name"
                error={form.formState.errors.name?.message}
                {...form.register('name')}
              />
            )}

            <Field
              id="email"
              label="Email"
              type="email"
              autoComplete="email"
              error={form.formState.errors.email?.message}
              {...form.register('email')}
            />

            <Field
              id="password"
              label="Password"
              type="password"
              autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
              error={form.formState.errors.password?.message}
              {...form.register('password')}
            />

            {serverError && (
              <p
                role="alert"
                className="rounded-lg border border-line bg-surface px-3 py-2 text-sm text-critical"
              >
                {serverError}
              </p>
            )}

            <Button type="submit" size="lg" className="mt-2" disabled={mutation.isPending}>
              {mutation.isPending ? 'Working…' : mode === 'signin' ? 'Sign in' : 'Create account'}
            </Button>
          </form>

          <p className="mt-6 text-sm text-ink-2">
            {mode === 'signin' ? "Don't have an account? " : 'Already have an account? '}
            <button
              type="button"
              onClick={swapMode}
              className="font-medium text-brand underline-offset-4 hover:underline"
            >
              {mode === 'signin' ? 'Sign up' : 'Sign in'}
            </button>
          </p>
        </div>
      </div>
    </div>
  )
}

const Field = ({
  id,
  label,
  error,
  ref,
  ...props
}: React.ComponentProps<typeof Input> & { id: string; label: string; error?: string }) => (
  <div className="flex flex-col gap-1.5">
    <Label htmlFor={id}>{label}</Label>
    <Input
      id={id}
      ref={ref}
      aria-invalid={Boolean(error)}
      aria-describedby={error ? `${id}-error` : undefined}
      {...props}
    />
    {error && (
      <p id={`${id}-error`} className="text-xs text-critical">
        {error}
      </p>
    )}
  </div>
)

function BrandPanel() {
  return (
    <div className="relative hidden overflow-hidden bg-surface lg:block">
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-60"
        style={{
          backgroundImage:
            'radial-gradient(60rem 40rem at 20% 15%, var(--tk-brand-soft), transparent 60%), radial-gradient(40rem 40rem at 85% 80%, var(--tk-brand-soft), transparent 65%)',
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          backgroundImage:
            'linear-gradient(var(--tk-grid) 1px, transparent 1px), linear-gradient(90deg, var(--tk-grid) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(70% 60% at 50% 45%, black, transparent)',
        }}
      />

      <div className="relative flex h-full flex-col justify-between p-12">
        <LogoMark className="size-12" />

        <div className="max-w-md">
          <h2 className="text-3xl font-semibold leading-tight tracking-tight">
            Every registration, followed up.
          </h2>
          <p className="mt-4 text-ink-2">
            The agent opens the conversation, answers fee and batch questions from your own
            course material, and hands a student to your team the moment they need a person.
          </p>
        </div>

        <span />
      </div>
    </div>
  )
}
